import { OptionalField, RequireOnly } from '@pelicargo/types';
import { ServiceTypePriority } from '@pelicargo/utils';
import { LineItem, Prisma } from '@prisma/client';
import { omit } from 'lodash';

export const getLineItemsTotal = (lineItems: RequireOnly<LineItem, 'cost'>[]) => {
  const total = lineItems?.map((item) => Number(item?.cost)).reduce((prev: number, curr: number) => prev + curr, 0);

  return total;
};

type OptionalInland = OptionalField<
  Prisma.QuoteVariantGetPayload<{
    select: {
      id: true;
      type: true;
      inland_service_quote_variant: {
        select: {
          line_items: {
            select: {
              cost: true;
            };
          };
        };
      };
      line_items: {
        select: {
          cost: true;
        };
      };
    };
  }>,
  'inland_service_quote_variant'
>;

export const getQuoteVariantTotal = <T extends OptionalInland>(variant: T) => {
  const inland = variant.inland_service_quote_variant;
  const inlandTotal = inland ? getLineItemsTotal(inland.line_items) : 0;
  const total = getLineItemsTotal(variant.line_items);
  return total + inlandTotal;
};

export const getBestQuoteVariants = <T extends OptionalInland>(quoteVariants: T[], selectedQuoteVariantId?: number) => {
  const quoteVariantsWithTotalCost = quoteVariants.map((variant) => ({
    ...variant,
    total_cost: getQuoteVariantTotal(variant),
  }));
  const selectedQuoteVariant = quoteVariantsWithTotalCost.find((variant) => variant.id === selectedQuoteVariantId);

  // Get the cheapest quote variant per service type
  const bestQuoteVariants =
    quoteVariantsWithTotalCost?.reduce(
      (acc, current) => {
        // If a selected quote variant is found, replace current qv with the same type
        if (selectedQuoteVariant && current.type === selectedQuoteVariant.type) {
          return [...acc.filter((v) => v.type !== current.type), selectedQuoteVariant];
        }

        const existingVariant = acc.find((v) => v.type === current.type);
        if (!existingVariant || current.total_cost < existingVariant.total_cost) {
          return [...acc.filter((v) => v.type !== current.type), current];
        }
        return acc;
      },
      [] as typeof quoteVariantsWithTotalCost,
    ) || [];

  const sortedQuoteVariants = bestQuoteVariants.sort((a, b) => {
    if (a.type != b.type) {
      return ServiceTypePriority.indexOf(a.type) - ServiceTypePriority.indexOf(b.type);
    }

    return a.total_cost - b.total_cost;
  });
  return sortedQuoteVariants.map((quoteVariant) => omit(quoteVariant, 'total_cost'));
};
